import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useExperiences } from "../context/ExperiencesContext";
import ExperiencesApi from "../services/ExperiencesAPI";
import ScoreVisualizations from "../components/answers/ScoreVisualizations";
import Score from "../components/dashboard/Score";
import { AppButton, Panel } from "../components/ui";
import "./QuestionAnswersPage.scss";

function safeDate(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

function formatDate(d) {
  if (!d) return "—";
  return d.toLocaleDateString(undefined, {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
}

function formatTime(d) {
  if (!d) return "";
  return d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function splitTopics(t) {
  if (!t) return [];
  if (Array.isArray(t)) return t.map(String).filter(Boolean);
  return String(t)
    .split(/[,|]/g)
    .map((s) => s.trim())
    .filter(Boolean);
}

export default function QuestionAnswersPage() {
  const [params] = useSearchParams();
  const insightId = params.get("insightId") || "";
  const experienceId = params.get("experienceId") || "";
  const q = params.get("q") || "";

  const { experiences } = useExperiences();

  const [hideBlank, setHideBlank] = useState(true);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());
  const [selectedBin, setSelectedBin] = useState(null);
  const [rows, setRows] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(false);
  const BIN_SIZE = 20;

  // Scroll to top when component mounts or question changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [insightId, q]);

  const toggleExpanded = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const getAnswerText = (r) =>
    (r.answer || r.transcript || r.studentAnswer || "").toString().trim();

  const getScoreNumber = (r) => {
    const n = Number(r.score);
    return Number.isFinite(n) ? n : null;
  };

  useEffect(() => {
    let cancelled = false;

    const fetchRows = async () => {
      if (!insightId) {
        setRows([]);
        setRowsLoading(false);
        return;
      }

      const experienceIds = experienceId
        ? [experienceId]
        : Array.isArray(experiences)
          ? experiences.map((exp) => exp?.experienceId).filter(Boolean)
          : null;

      if (!experienceIds) {
        return;
      }

      if (experienceIds.length === 0) {
        setRows([]);
        setRowsLoading(false);
        return;
      }

      setRowsLoading(true);

      try {
        const allAssessments = [];
        const BATCH_SIZE = 10;

        for (let i = 0; i < experienceIds.length; i += BATCH_SIZE) {
          const batch = experienceIds.slice(i, i + BATCH_SIZE);

          const batchResults = await Promise.all(
            batch.map(async (expId) => {
              try {
                const query = new URLSearchParams({ experienceId: expId });
                if (insightId) {
                  query.set("insightId", insightId);
                }

                const res = await ExperiencesApi.get(`assessments?${query.toString()}`, {
                  requiresAuth: true,
                });
                const data = res?.data;

                if (!Array.isArray(data)) return [];

                return data.map((assessment) => ({
                  ...assessment,
                  experienceId: assessment?.experienceId ?? expId,
                }));
              } catch {
                return [];
              }
            })
          );

          batchResults.forEach((batchItems) => {
            if (Array.isArray(batchItems)) {
              allAssessments.push(...batchItems);
            }
          });
        }

        if (cancelled) return;

        const nextRows = allAssessments
          .map((assessment) => ({
            timestamp: assessment.lastModified || assessment.createdAt || "",
            score: Number(assessment.score ?? 0),
            answer: (assessment.studentAnswer ?? assessment.transcript ?? "").toString(),
            transcript: (assessment.studentAnswer ?? assessment.transcript ?? "").toString(),
            summary: assessment.summary ?? "",
            feedback: assessment.aiResponse ?? assessment.student_evaluation ?? "",
            topics: assessment.topics ?? "",
            environmentId: assessment.environmentId ?? null,
            sessionCode: assessment.sessionCode ?? assessment.assessmentId ?? null,
            experienceId: assessment.experienceId ?? null,
            insightId: assessment.insightId ?? null,
          }))
          .sort((a, b) => String(b.timestamp).localeCompare(String(a.timestamp)));

        setRows(nextRows);
      } catch {
        if (!cancelled) {
          setRows([]);
        }
      } finally {
        if (!cancelled) {
          setRowsLoading(false);
        }
      }
    };

    fetchRows();

    return () => {
      cancelled = true;
    };
  }, [experienceId, experiences, insightId]);

  const loading = rowsLoading || experiences === null;

  const avgScore = useMemo(() => {
    if (!rows.length) return null;
    const scored = rows.map((r) => getScoreNumber(r)).filter((n) => typeof n === "number");
    if (!scored.length) return null;
    return Math.round(scored.reduce((a, b) => a + b, 0) / scored.length);
  }, [rows]);

  const blankCount = useMemo(() => rows.filter((r) => !getAnswerText(r)).length, [rows]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      const ans = getAnswerText(r);
      const score = getScoreNumber(r);

      if (hideBlank && !ans) return false;
      if (selectedBin && score != null) {
        const inBin = score >= selectedBin.min && (score < selectedBin.max || (selectedBin.max === 100 && score === 100));
        if (!inBin) return false;
      }
      if (search && !ans.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [rows, hideBlank, search, selectedBin]);

  const resultsLabel = loading
    ? "Loading responses…"
    : `${filtered.length.toLocaleString()} shown${
        filtered.length !== rows.length ? ` of ${rows.length.toLocaleString()}` : ""
      }`;

  return (
    <div className="qaPage">
      <div className="qaContainer">
        <Link className="qaBackLink" to="/dashboard">
          ← Back to Dashboard
        </Link>

        <div className="qaTopGrid">
          <Panel className="qaHeader" row={false}>
            <div className="qaHeaderMeta">Insight Question</div>
            <div className="qaHeaderId">Insight ID {insightId || "—"}</div>
            <h1 className="qaHeaderQuestion">{q || "(no question provided)"}</h1>
          </Panel>

          <Panel className="qaSection qaSection--viz" row={false}>
            <div className="qaSectionHeader">
              <div>
                <div className="qaSectionEyebrow">Overview</div>
                <div className="qaSectionTitle">Response distribution</div>
              </div>
            </div>
            <div className="qaSectionBody">
              <ScoreVisualizations
                rows={rows}
                getAnswerText={getAnswerText}
                getScoreNumber={getScoreNumber}
                selectedBin={selectedBin}
                setSelectedBin={setSelectedBin}
                binSize={BIN_SIZE}
                avgScore={avgScore}
                blankCount={blankCount}
              />
            </div>
          </Panel>
        </div>

        <Panel className="qaSection qaSection--responses" row={false}>
          <div className="qaSectionHeader qaSectionHeader--responses">
            <div>
              <div className="qaSectionEyebrow">Responses</div>
              <div className="qaSectionTitle">Student responses</div>
              <div className="qaSectionSubtitle">{resultsLabel}</div>
            </div>
          </div>
          <div className="qaToolbar">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="qaSearch"
              placeholder="Search responses"
            />
            <AppButton
              className="qaPill qaBlankToggle"
              onClick={() => setHideBlank((v) => !v)}
              type="button"
              kind="secondary"
              aria-pressed={hideBlank}
            >
              {hideBlank ? "Show blank responses" : "Hide blank responses"}
            </AppButton>
          </div>

          {loading ? (
            <div className="qaEmpty">Loading answers…</div>
          ) : rows.length === 0 ? (
            <div className="qaEmpty">
              No responses found for this question.
            </div>
          ) : (
            <div className="qaTable app-row-list">
              <div className="qaTableHeader">
                <div className="qaTh">DATE</div>
                <div className="qaTh">NAME</div>
                <div className="qaTh">SCORE</div>
                <div className="qaTh">ANSWER</div>
                <div className="qaTh qaThAction" />
              </div>

              {filtered.map((r, idx) => {
                const key = `${r.timestamp || ""}-${r.sessionCode || ""}-${idx}`;
                const isOpen = expanded.has(key);
                const answer = getAnswerText(r);
                const isBlank = !answer;
                const d = safeDate(r.timestamp);
                const topics = splitTopics(r.topics);
                const hasDetails = Boolean(r.summary || r.feedback || topics.length);

                return (
                  <Panel key={key} className="qaTr" hoverable>
                    <div className="qaTd qaTdDate">
                      <div className="qaDateMain">{formatDate(d)}</div>
                      <div className="qaDateSub">{formatTime(d)}</div>
                    </div>
                    <div className="qaTd qaTdName">
                      <div className="qaNameSub">Anonymous</div>
                      <div className="qaNameMain">{r.sessionCode ? r.sessionCode.slice(0, 4) : "—"}</div>
                    </div>
                    <div className="qaTd qaTdScore">
                      {isBlank ? (
                        <div className="qaScoreEmpty">—</div>
                      ) : (
                        <Score score={Math.round(r.score || 0)} />
                      )}
                    </div>
                    <div className="qaTd qaTdAnswer">
                      <div className={`qaAnswer ${isBlank ? "isBlank" : ""}`}>
                        {answer || "No response provided"}
                      </div>
                      {hasDetails && isOpen && (
                        <div className="qaDetails">
                          {r.summary && (
                            <div className="qaDetailBlock">
                              <div className="qaDetailLabel">SUMMARY</div>
                              <div className="qaDetailText">{r.summary}</div>
                            </div>
                          )}
                          {r.feedback && (
                            <div className="qaDetailBlock">
                              <div className="qaDetailLabel">AI FEEDBACK</div>
                              <div className="qaDetailText">{r.feedback}</div>
                            </div>
                          )}
                          {topics.length > 0 && (
                            <div className="qaDetailBlock">
                              <div className="qaDetailLabel">TOPICS</div>
                              <div className="qaTopics">
                                {topics.map((t) => (
                                  <span key={t} className="qaTopic">{t}</span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="qaTd qaTdAction">
                      {hasDetails ? (
                        <AppButton
                          className={`qaPill ${isOpen ? "isActive" : ""}`}
                          type="button"
                          kind="secondary"
                          onClick={() => toggleExpanded(key)}
                        >
                          {isOpen ? "HIDE" : "VIEW"}
                        </AppButton>
                      ) : (
                        <div className="qaNoAction">—</div>
                      )}
                    </div>
                  </Panel>
                );
              })}

              {filtered.length === 0 && (
                <div className="qaEmpty">No results match your filters.</div>
              )}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
